import React, { useState } from 'react';
import { useSectionInView } from './useSectionInView';

// Main App Component
const App = () => {
  // Utility function to scroll to a section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Section in-view hooks (must be at the top level)
  const [homeRef, homeInView] = useSectionInView();
  const [aboutRef, aboutInView] = useSectionInView();
  const [featuresRef, featuresInView] = useSectionInView();
  const [previewRef, previewInView] = useSectionInView();
  const [downloadRef, downloadInView] = useSectionInView();
  const [contactRef, contactInView] = useSectionInView();

  // Contact form state
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (formData.name && formData.email && formData.message) {
      console.log('Form data submitted:', formData);
      setIsFormSubmitted(true);
    } else {
      alert('Please fill out all fields.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white font-inter">
      {/* Header Section */}
      <header className="fixed top-0 left-0 w-full z-50 p-4 shadow-lg"
        style={{
          background: 'linear-gradient(90deg, #6094EA, #AA4FF4, #FF00FF, #FF35A3)'
        }}
      >
        <nav className="container mx-auto flex justify-between items-center px-4">
          <div className="flex items-center space-x-2">
            <img
              src="/pcpb_logo.png"
              alt="PCPocketBuilder Logo"
              className="h-8 w-8 rounded-full"
              onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/32x32/333333/FFFFFF?text=PC'; }}
            />
            <div className="text-2xl font-bold">
              PCPocketBuilder
            </div>
          </div>
          <ul className="hidden md:flex space-x-8">
            <li>
              <a href="#home" onClick={() => scrollToSection('home')} className="hover:text-gray-200 transition-colors duration-300">Home</a>
            </li>
            <li>
              <a href="#about" onClick={() => scrollToSection('about')} className="hover:text-gray-200 transition-colors duration-300">About</a>
            </li>
            <li>
              <a href="#features" onClick={() => scrollToSection('features')} className="hover:text-gray-200 transition-colors duration-300">Features</a>
            </li>
            <li>
              <a href="#preview" onClick={() => scrollToSection('preview')} className="hover:text-gray-200 transition-colors duration-300">Preview</a>
            </li>
            <li>
              <a href="#download" onClick={() => scrollToSection('download')} className="hover:text-gray-200 transition-colors duration-300">Download APK</a>
            </li>
            <li>
              <a href="#contact" onClick={() => scrollToSection('contact')} className="hover:text-gray-200 transition-colors duration-300">Contact</a>
            </li>
          </ul>
          <div className="md:hidden">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" ref={homeRef} className={`relative h-screen flex items-center justify-center pt-24 transition-opacity duration-700 ${homeInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500`}
        style={{
          background: 'linear-gradient(135deg, #6094EA, #AA4FF4, #FF00FF, #FF35A3)'
        }}
      >
        <div className="container mx-auto text-center px-4">
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight animate-fade-in">
            Build Your Dream PC
          </h1>
          <p className="text-xl md:text-2xl mb-8 animate-fade-in-delay-1">
            Discover the best components for unparalleled performance and gaming.
          </p>
          <button
            onClick={() => scrollToSection('features')}
            className="px-8 py-4 bg-white text-gray-900 font-bold rounded-full text-lg shadow-xl hover:scale-105 transform transition-transform duration-300 ease-in-out"
          >
            Explore Components
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" ref={aboutRef} className={`py-20 bg-gray-800 transition-opacity duration-700 ${aboutInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-8">About PCPocketBuilder</h2>
          <p className="text-lg leading-relaxed max-w-3xl mx-auto">
            PCPocketBuilder is your ultimate destination for high-quality PC components. We offer a curated selection of CPUs, GPUs, motherboards, RAM, storage, and peripherals from leading brands to help you build or upgrade your ideal system. Our mission is to empower enthusiasts and gamers with the tools they need to achieve peak performance.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" ref={featuresRef} className={`py-20 bg-gray-900 transition-opacity duration-700 ${featuresInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-12">Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature Card 1 */}
            <div className="bg-gray-800 p-8 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300">
              <div className="text-5xl mb-4 text-pink-400">⚡</div>
              <h3 className="text-2xl font-semibold mb-4">High Performance</h3>
              <p className="text-md">
                Unleash the full potential of your system with top-tier CPUs and GPUs.
              </p>
            </div>
            {/* Feature Card 2 */}
            <div className="bg-gray-800 p-8 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300">
              <div className="text-5xl mb-4 text-blue-400">🛡️</div>
              <h3 className="text-2xl font-semibold mb-4">Reliable Quality</h3>
              <p className="text-md">
                Only the most durable and reliable components make it into our selection.
              </p>
            </div>
            {/* Feature Card 3 */}
            <div className="bg-gray-800 p-8 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300">
              <div className="text-5xl mb-4 text-purple-400">⚙️</div>
              <h3 className="text-2xl font-semibold mb-4">Easy Compatibility</h3>
              <p className="text-md">
                Tools to help you ensure all your chosen components work together seamlessly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Section */}
      <section id="preview" ref={previewRef} className={`py-20 bg-gray-800 transition-opacity duration-700 ${previewInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-8">App Preview</h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto">
            Get a glimpse of our intuitive mobile app where you can browse, compare, and select your PC components with ease.
          </p>
          <div className="bg-gray-900 rounded-xl p-8 shadow-xl max-w-2xl mx-auto">
            <div className="flex flex-col md:flex-row justify-center items-center gap-8">
              <img
                src="/build_it_way.png"
                alt="Build It Way Preview"
                className="w-56 h-[28rem] object-contain rounded-lg shadow-md bg-gray-800"
              />
              <img
                src="/home.png"
                alt="Home Preview"
                className="w-56 h-[28rem] object-contain rounded-lg shadow-md bg-gray-800"
              />
              <img
                src="/shop.png"
                alt="Shop Preview"
                className="w-56 h-[28rem] object-contain rounded-lg shadow-md bg-gray-800"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Download APK Section */}
      <section id="download" ref={downloadRef} className={`py-20 bg-gray-900 text-center transition-opacity duration-700 ${downloadInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-8">Download Our App</h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto">
            Get the full PCPocketBuilder experience on your Android device. Download our official APK now!
          </p>
          <a
            href="/PCPB_apk.apk"
            className="inline-block px-10 py-5 font-bold rounded-full text-lg shadow-2xl transition-all duration-300 transform hover:scale-105"
            style={{
              background: 'linear-gradient(90deg, #FF35A3, #FF00FF)',
              color: 'white'
            }}
            download
          >
            Download APK
          </a>
          <div className="mt-4 text-gray-400 text-sm">
            <p>Version: 0.1.0</p>
            <p>Size: 14 MB</p>
          </div>
          <p className="mt-4 text-sm text-gray-400">
            (Requires Android 5.0+ and up)
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" ref={contactRef} className={`py-20 bg-gray-800 transition-opacity duration-700 ${contactInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-8">Get in Touch</h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto">
            Have questions or need support? Feel free to reach out to us!
          </p>
          <div className="max-w-md mx-auto">
            {isFormSubmitted ? (
              <div className="bg-gray-700 p-8 rounded-xl shadow-lg text-center">
                <h3 className="text-2xl font-semibold mb-4 text-white">Thank You!</h3>
                <p className="text-lg text-gray-300">We have received your message.</p>
              </div>
            ) : (
              <form
                name="contact"
                method="POST"
                data-netlify="true"
                onSubmit={handleFormSubmit}
                className="grid grid-cols-1 gap-6"
              >
                <input type="hidden" name="form-name" value="contact" />
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full p-4 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-4 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  required
                />
                <textarea
                  name="message"
                  placeholder="Your Message"
                  rows="6"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full p-4 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-pink-500 resize-y"
                  required
                ></textarea>
                <button
                  type="submit"
                  className="px-8 py-4 font-bold rounded-full text-lg shadow-xl transition-all duration-300 transform hover:scale-105"
                  style={{
                    background: 'linear-gradient(90deg, #6094EA, #AA4FF4)',
                    color: 'white'
                  }}
                >
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-950 text-center text-gray-400">
        <p>&copy; {new Date().getFullYear()} PCPocketBuilder. All rights reserved.</p>
        <div className="mt-4">
          <a
            href="https://www.facebook.com/PCPB.page"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-gray-400 hover:text-blue-500 transition-colors duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="text-blue-600"
            >
              <path d="M12 2.04C6.51 2.04 2.04 6.51 2.04 12c0 5.08 3.7 9.27 8.52 9.94v-7.07H7.42V12h3.14V9.32c0-3.1 1.89-4.79 4.65-4.79 1.32 0 2.45.24 2.79.35v3.1h-1.84c-1.52 0-1.81.72-1.81 1.77V12h3.48l-.57 3.51h-2.91v7.07c4.82-.67 8.52-4.86 8.52-9.94 0-5.49-4.47-9.96-9.96-9.96z"/>
            </svg>
            <span>Follow us on Facebook</span>
          </a>
        </div>
      </footer>
    </div>
  );
};

export default App; 